/* eslint-disable @typescript-eslint/no-explicit-any */
import {Box, Button, Dialog, Typography} from '@mui/material';
import {useRouter} from 'next/router';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  CrossOutlined,
  HiristDoneIcon,
  ProfileUpdateInfoIcon,
  IImjobsDoneIcon,
  IImjobsProfileUpdateInfoIcon,
} from '@jobseeker/lotus/assets/icons';
import {useEffect, useState} from 'react';
import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';
import {
  getUbaPageName,
  openUrl,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import Link from 'next/link';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

type ProfileUpdateModal = {
  isJobFeed: boolean;
  handleForceBannerClose?: () => void;
  forceEnable?: boolean;
  isProfileUpdateModalOpen?: boolean;
  isResumeAutoGeneratedCompleted?: boolean;
  redirectTo?: string;
};

const iconMap: Record<string, string> = {
  success: isAppIimjobs ? IImjobsDoneIcon : HiristDoneIcon,
  info: isAppIimjobs ? IImjobsProfileUpdateInfoIcon : ProfileUpdateInfoIcon,
};

const cohortKeyMapper: Record<string, string> = {
  profileUpdateSuccess: 'profileUpdateSuccess',
};

const ProfileUpdateModal = ({
  isJobFeed,
  handleForceBannerClose,
  forceEnable = false,
  isProfileUpdateModalOpen = false,
  isResumeAutoGeneratedCompleted = false,
  redirectTo,
}: ProfileUpdateModal) => {
  const router = useRouter();
  const ref = Array.isArray(router.query?.ref)
    ? router.query.ref[0]
    : router.query?.ref;

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const activeCohortKey =
    ref === 'update-profile' ? 'profileUpdateSuccess' : null;

  const functionalAreaFix = !isAppIimjobs && isProfileUpdateModalOpen;

  const interceptor: any = useInterceptor();

  const {
    jobfeed: {profileUpdateCohorts},
  } = interceptor;

  const mappedKey = activeCohortKey
    ? cohortKeyMapper[activeCohortKey]
    : isResumeAutoGeneratedCompleted
      ? 'resumeAutoGenerated'
      : functionalAreaFix
        ? 'functionalAreaFix'
        : null;
  const content = mappedKey ? profileUpdateCohorts[mappedKey] : null;
  const Icon = iconMap[content?.icon];
  const trackProfileUpdateOverlay = (
    actionType: 'view' | 'click',
    actionSrc: string = ''
  ) => {
    const cohortType = mappedKey || '';
    const eventName =
      actionType === 'view'
        ? 'profileUpdateOverlayView'
        : 'profileUpdateOverlayClick';

    if (typeof window === 'undefined' || !(window as any)?.tracking) return;

    (window as any).tracking.doTracking({
      uba: {
        eventName,
        keyNames: {
          sessionUsername: sessionStorage.getItem('userId') || '',
          cohortType,
          deviceType: sessionStorage.getItem('deviceType') || 'web',
          actionType,
          pageName: getUbaPageName(),
          actionSrc,
        },
      },
    });
  };

  const handleClose = () => {
    console.log('🚀 ~ handleClose ~ handleClose:', handleClose);

    if (mappedKey) {
      trackProfileUpdateOverlay('click', 'Closed');
    }

    if (isResumeAutoGeneratedCompleted) {
      TrackEventUBA('listBannerClick', 'click', {
        ref: router.query.ref || '',
        pageName: getUbaPageName(),
        sessionUsername: sessionStorage.getItem('userId') || '',
        bannerName: 'autocvnotbuild',
        clickSection: 'Cross',
      });
      window.location.reload();
      return;
    }

    handleForceBannerClose && handleForceBannerClose();
    setDialogOpen(false);
  };

  useEffect(() => {
    if (isProfileUpdateModalOpen) {
      setDialogOpen(true);
    } else if (forceEnable || activeCohortKey) {
      setDialogOpen(!!activeCohortKey);
    }
  }, [activeCohortKey, forceEnable, isProfileUpdateModalOpen]);

  useEffect(() => {
    if (dialogOpen && isResumeAutoGeneratedCompleted) {
      TrackEventUBA('listBannerView', 'view', {
        ref: router.query.ref || '',
        pageName: getUbaPageName(),
        sessionUsername: sessionStorage.getItem('userId') || '',
        bannerName: 'autocvnotbuild',
      });
    }
  }, [dialogOpen, isResumeAutoGeneratedCompleted]);

  useEffect(() => {
    if (dialogOpen && mappedKey) {
      trackProfileUpdateOverlay('view');
    }
  }, [dialogOpen, mappedKey]);

  const parseTextWithLinks = (text: string): React.ReactNode[] => {
    const regex = /{{([^}|]+)\|([^}]+)}}/g;
    const parts: React.ReactNode[] = [];

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      const [fullMatch, displayText, url] = match;
      const matchStart = match.index;

      // Push preceding text
      if (matchStart > lastIndex) {
        parts.push(text.slice(lastIndex, matchStart));
      }

      // Push link
      parts.push(
        <Link
          key={url + matchStart}
          href={url}
          passHref
          style={{
            textDecoration: 'none',
          }}
        >
          <Typography
            component="span"
            sx={{
              color: 'primary.main',
              fontWeight: 500,
              textDecoration: 'none',
              cursor: 'pointer',
              mx: 0.5,
            }}
          >
            {displayText}
          </Typography>
        </Link>
      );

      lastIndex = match?.index + fullMatch?.length;
    }

    // Push remaining text
    if (lastIndex < text?.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };

  return (
    <Dialog
      open={dialogOpen}
      onClose={() => {
        handleClose();
      }}
      fullWidth
      sx={{
        '.MuiDialog-paper': {
          maxWidth: 520,
          borderRadius: 5,
        },
        '.mui-style-1bvc4cc': {
          paddingLeft: 0,
          paddingRight: 0,
        },
        '.mui-style-qggrpf': {
          paddingLeft: 5,
          paddingRight: 5,
        },
      }}
    >
      <Box display={'flex'} justifyContent={'flex-end'}>
        <Box
          onClick={() => {
            handleClose();
          }}
          sx={{
            cursor: 'pointer',
            position: 'relative',
            width: 50,
            height: 50,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1.5,
            mt: 1,
            '&:hover': {
              boxShadow: '0 6px 14px 0 rgba(0, 0, 0, 0.09)',
            },
          }}
        >
          <Image width={20} height={20} src={CrossOutlined} alt={'cross'} />
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        mt={1}
        px={{xxs: 5, md: 10}}
        pb={1}
      >
        <Image src={Icon} alt="done" width={48} height={48} />
        <Typography
          mt={2}
          fontSize={18}
          fontWeight={500}
          color={'#333333'}
          lineHeight="30px"
          paddingLeft={4.3}
          paddingRight={4.3}
        >
          {content?.header}
        </Typography>
        <Typography
          lineHeight="20px"
          fontSize={{xxs: 12, md: 14}}
          fontWeight={500}
          color="#333333"
          mt={1}
          mb={1}
        >
          {parseTextWithLinks(content?.body)}
        </Typography>

        <Typography
          mt={1}
          fontSize={{xxs: 12, md: 14}}
          fontWeight={500}
          lineHeight="20px"
          mb={2}
          color="#7E7E7E"
        >
          {parseTextWithLinks(content?.subText)}
        </Typography>

        {content?.footer && (
          <Button
            sx={{
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: '4px',
              padding: '5px 20px',
              display: 'flex',
              mt: 1,
              mb: 6,
              alignItems: 'center',
            }}
            onClick={() => {
              setDialogOpen(false);

              if (mappedKey) {
                trackProfileUpdateOverlay('click', content?.footer || '');
              }

              if (isResumeAutoGeneratedCompleted) {
                TrackEventUBA('listBannerClick', 'click', {
                  ref: router.query.ref || '',
                  pageName: getUbaPageName(),
                  sessionUsername: sessionStorage.getItem('userId') || '',
                  bannerName: 'autocvnotbuild',
                  clickSection: 'Upload',
                });
              }

              if (content?.buttonRedirectUrl) {
                const url = new URL(
                  `/${content.buttonRedirectUrl}`,
                  window.location.origin
                );
                url.searchParams.set('via', 'popup');
                if (redirectTo) {
                  url.searchParams.set('redirectTo', redirectTo);
                }

                const fullUrl = url.toString();

                openUrl(fullUrl, '_self');
              }
            }}
          >
            <Typography
              className="text"
              sx={{
                fontSize: 16,
                color: 'primary.main',
                fontWeight: 500,
                lineHeight: '24px',
              }}
            >
              {content?.footer}
            </Typography>
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

export default ProfileUpdateModal;
